import { useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { SignupPoint } from "../../../interface/dashboard.interface";
import ChartTooltip from "./ChartTooltip";
import DashCard from "./DashCard";
import { fmtCompact } from "./format";

const SERIES = [
  { key: "male", name: "Male", color: "#7600BC" },
  { key: "female", name: "Female", color: "#F3A838" },
  { key: "other", name: "Other", color: "#94A3B8" },
];

const Segmented = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => (
  <div className="inline-flex rounded-lg border border-hcBorder p-0.5 text-xs font-medium" role="group">
    {[
      ["total", "Total"],
      ["gender", "By gender"],
    ].map(([v, l]) => (
      <button
        key={v}
        type="button"
        onClick={() => onChange(v)}
        aria-pressed={value === v}
        className={`rounded-md px-2.5 py-1 transition-colors ${
          value === v ? "bg-hc_F4EBFA text-honeConnectPurple" : "text-hcMuted hover:text-honeConnectBlack"
        }`}
      >
        {l}
      </button>
    ))}
  </div>
);

const SignupsChart = ({ data, className }: { data?: SignupPoint[]; className?: string }) => {
  const [mode, setMode] = useState("total");
  const total = data?.reduce((s, d) => s + (d.total ?? 0), 0);

  return (
    <DashCard
      title="Sign-ups"
      subtitle={total !== undefined ? `${total.toLocaleString()} in this range` : undefined}
      action={<Segmented value={mode} onChange={setMode} />}
      empty={!data?.length}
      className={className}
    >
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
            <defs>
              {[{ key: "total", color: "#7600BC" }, ...SERIES].map((s) => (
                <linearGradient key={s.key} id={`grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={s.color} stopOpacity={0.22} />
                  <stop offset="100%" stopColor={s.color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid vertical={false} stroke="#EEF0F3" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#8A8F9C" }} />
            <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#8A8F9C" }} tickFormatter={(v) => fmtCompact(v)} width={44} />
            <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#E8EAEE" }} />
            {mode === "total" ? (
              <Area type="monotone" dataKey="total" name="Sign-ups" stroke="#7600BC" strokeWidth={2} fill="url(#grad-total)" />
            ) : (
              SERIES.map((s) => (
                <Area
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.name}
                  stackId="g"
                  stroke={s.color}
                  strokeWidth={2}
                  fill={`url(#grad-${s.key})`}
                />
              ))
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {mode === "gender" && (
        <div className="mt-3 flex flex-wrap gap-4 text-xs text-hcMuted">
          {SERIES.map((s) => (
            <span key={s.key} className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />
              {s.name}
            </span>
          ))}
        </div>
      )}
    </DashCard>
  );
};

export default SignupsChart;
